import React, { useState, useMemo, useEffect } from 'react';
import isEqual from 'lodash/isEqual';
import isNull from 'lodash/isNull';
import EmailAlertsContext, { useEmailAlertsContext } from '@dataroom/email-alerts/contexts/EmailAlertsContext';
import Modal from '@dealroadshow/uikit-modal';
import Alerts, { AlertsType } from './Alerts';
import DigestAlerts from './DigestAlerts';
import QuestionAlerts from './QuestionAlerts';
import Button, { ButtonVariantType } from '@dealroadshow/uikit-button';
import { setActiveAlerts, setActiveDigestAlerts } from './helpers';
import { useDataroomListingContext } from '../../../../contexts/DataroomListingContext';
import { IDataroomListItem } from '../../../../domain/vo/DataroomListItem';
import { IFileUploadNotificationSettingsPayload } from '@dataroom/email-alerts/domain/vo/FileUploadEmailAlerts';
import {
  IStagingFileUploadNotificationSettingsPayload,
} from '@dataroom/email-alerts/domain/vo/StagingFileUploadEmailAlerts';
import { IDigestNotificationSettingsPayload } from '@dataroom/email-alerts/domain/vo/DigestEmailAlerts';
import { IAccessRequestNotificationSettingsPayload } from '@dataroom/email-alerts/domain/vo/AccessRequestEmailAlerts';
import {
  IQuestionsNotificationSettingsPayload,
  IQuestionsRealtimeNotificationSettingsPayload,
} from '@dataroom/email-alerts/domain/vo/QuestionsEmailAlerts';
import { QuestionAlertsType } from '@dataroom/email-alerts/ui/QuestionAlertsTypeSwitcher';
import isDefined from '@framework/data-helpers/isDefined';
import styles from './alertsModal.module.scss';
import { QuestionsPermission } from '../../../../domain/questions/permissions';

interface IProps {
  dataroom: IDataroomListItem,
  closeModal: () => void,
}

const isEmailAlertsEmpty = (alerts: object | null) => alerts && Object.values(alerts)
  .some(isNull);

const AlertsModal = ({
  dataroom,
  closeModal,
}: IProps) => {
  const {
    getCollection,
  } = useDataroomListingContext();

  const [
    uploadNotificationSettings,
    setUploadNotificationSettings,
  ] = useState<IFileUploadNotificationSettingsPayload | null>(null);
  const [
    stagingUploadNotificationSettings,
    setStagingUploadNotificationSettings,
  ] = useState<IStagingFileUploadNotificationSettingsPayload | null>(null);
  const [
    digestNotificationSettings,
    setDigestNotificationSettings,
  ] = useState<IDigestNotificationSettingsPayload | null>(null);
  const [
    accessRequestNotificationSettings,
    setAccessRequestNotificationSettings,
  ] = useState<IAccessRequestNotificationSettingsPayload | null>(null);
  const [
    questionNotificationSettings,
    setQuestionNotificationSettings,
  ] = useState<IQuestionsNotificationSettingsPayload | null>(null);
  const [
    questionRealtimeNotificationSettings,
    setQuestionRealtimeNotificationSettings,
  ] = useState<IQuestionsRealtimeNotificationSettingsPayload | null>(null);
  const [
    questionNotificationTypeValue,
    setQuestionNotificationTypeValue,
  ] = useState(QuestionAlertsType.All);
  const [questionRealtimeValidationError, setQuestionRealtimeValidationError] = useState('');

  const {
    isFetching,
    updateSettings,
  } = useEmailAlertsContext();

  const {
    name: dataroomName,
    displayName,
    notificationSettings,
    qnaNotificationSettings,
    timezone,
    allowedActions,
    qnaPermission,
  } = dataroom;

  const isQuestionManager = [
    QuestionsPermission.FullAccessManager,
    QuestionsPermission.ViewOnlyManager,
  ].includes(qnaPermission);

  const {
    realtime = {
      enabled: undefined,
    },
    daily = {
      enabled: undefined,
      time: undefined,
    },
    weekly = {
      enabled: undefined,
      time: undefined,
      day: undefined,
    },
    stagingRealtime = {
      enabled: undefined,
    },
    stagingDaily = {
      enabled: undefined,
      time: undefined,
    },
    stagingWeekly = {
      enabled: undefined,
      time: undefined,
      day: undefined,
    },
    digestDaily = {
      enabled: undefined,
      time: undefined,
      day: undefined,
    } as any,
    digestWeekly = {
      enabled: undefined,
      time: undefined,
      day: undefined,
    },
    accessRequestRealtime = {
      enabled: undefined,
    },
    accessRequestDaily = {
      enabled: undefined,
      time: undefined,
    },
    accessRequestWeekly = {
      enabled: undefined,
      time: undefined,
      day: undefined,
    },
  } = notificationSettings![0] || {};

  const {
    daily: questionDaily = {
      enabled: false,
      time: undefined,
    },
    weekly: questionWeekly = {
      enabled: false,
      time: undefined,
      day: undefined,
    },
    allQuestions = true,
    realtime: questionRealtime = {
      enabled: false,
      realtime: undefined,
    },
  } = qnaNotificationSettings || {};

  const uploadAlertsInitialValues = useMemo(() => ({
    isActiveAlerts: setActiveAlerts(realtime.enabled, daily.enabled, weekly.enabled),
    isRealTime: realtime.enabled,
    isDaily: daily.enabled,
    isWeekly: weekly.enabled,
    weeklyTime: weekly.time || '01:00:00',
    weeklyDay: weekly.day || 'Sunday',
    dailyTime: daily.time || '01:00:00',
  }), [notificationSettings![0]]);

  const stagingUploadAlertsInitialValues = useMemo(() => ({
    isActiveAlerts: setActiveAlerts(stagingRealtime.enabled, stagingDaily.enabled, stagingWeekly.enabled),
    isRealTime: stagingRealtime.enabled,
    isDaily: stagingDaily.enabled,
    isWeekly: stagingWeekly.enabled,
    weeklyTime: stagingWeekly.time || '15:00:00',
    weeklyDay: stagingWeekly.day || 'Friday',
    dailyTime: stagingDaily.time || '15:00:00',
  }), [notificationSettings![0]]);

  const digestAlertsInitialValues = useMemo(() => ({
    isActiveDigestAlerts: setActiveDigestAlerts(digestDaily.enabled, digestWeekly.enabled),
    isDigestDaily: digestDaily.enabled,
    isDigestWeekly: digestWeekly.enabled,
    digestDailyTime: digestDaily.time || '01:00:00',
    digestWeeklyTime: digestWeekly.time || '01:00:00',
    digestWeeklyDay: digestWeekly.day || 'Sunday',
  }), [notificationSettings![0]]);

  const accessRequestAlertsInitialValues = useMemo(() => ({
    isActiveAlerts: setActiveAlerts(
      accessRequestRealtime.enabled,
      accessRequestDaily.enabled,
      accessRequestWeekly.enabled,
    ),
    isRealTime: accessRequestRealtime.enabled,
    isDaily: accessRequestDaily.enabled,
    isWeekly: accessRequestWeekly.enabled,
    weeklyTime: accessRequestWeekly.time || '17:00:00',
    weeklyDay: accessRequestWeekly.day || 'Friday',
    dailyTime: accessRequestDaily.time || '17:00:00',
  }), [notificationSettings![0]]);

  const questionAlertsInitialValues = useMemo(() => ({
    isActiveQuestionAlerts: setActiveAlerts(questionRealtime.enabled, questionDaily.enabled, questionWeekly.enabled),
    isQuestionRealTime: questionRealtime.enabled,
    isQuestionDaily: questionDaily.enabled,
    isQuestionWeekly: questionWeekly.enabled,
    questionDailyTime: (Array.isArray(questionDaily.time) ? questionDaily.time[0] : questionDaily.time) || '01:00:00',
    questionWeeklyTime: questionWeekly.time || '01:00:00',
    questionWeeklyDay: questionWeekly.day || 'Sunday',
  }), [qnaNotificationSettings]);

  const questionRealtimeAlertsInitialValues = useMemo(() => ({
    questionSubmitted: questionRealtime.enabled ? questionRealtime.realtime?.questionSubmitted : true,
    replySubmitted: questionRealtime.enabled ? questionRealtime.realtime?.replySubmitted : true,
    noteSubmitted: questionRealtime.realtime?.noteSubmitted || false,
    questionAssigned: questionRealtime.realtime?.questionAssigned || false,
  }), [qnaNotificationSettings]);

  useEffect(() => {
    setQuestionNotificationTypeValue(
      isDefined(allQuestions) ? Number(allQuestions) : QuestionAlertsType.All,
    );
  }, [allQuestions]);

  const isUploadAlertsDirty = (
    !!uploadNotificationSettings &&
    !isEqual(uploadNotificationSettings, uploadAlertsInitialValues)
  );

  const isStagingUploadAlertsDirty = (
    !!stagingUploadNotificationSettings &&
    !isEqual(stagingUploadNotificationSettings, stagingUploadAlertsInitialValues)
  );

  const isDigestAlertsDirty = (
    !!digestNotificationSettings &&
    !isEqual(digestNotificationSettings, digestAlertsInitialValues)
  );

  const isAccessRequestAlertsDirty = (
    !!accessRequestNotificationSettings &&
    !isEqual(accessRequestNotificationSettings, accessRequestAlertsInitialValues)
  );

  const isQuestionAlertsDirty = (
    !!questionNotificationSettings && (
      !isEqual(questionNotificationSettings, questionAlertsInitialValues) ||
      !!allQuestions !== !!questionNotificationTypeValue
    )
  );

  const isQuestionRealtimeAlertsDirty = (
    questionNotificationSettings?.isQuestionRealTime &&
    !isEqual(questionRealtimeNotificationSettings, questionRealtimeAlertsInitialValues)
  );

  const isUploadAlertsEmpty = isEmailAlertsEmpty(uploadNotificationSettings);
  const isStagingUploadAlertsEmpty = isEmailAlertsEmpty(stagingUploadNotificationSettings);
  const isDigestAlertsEmpty = isEmailAlertsEmpty(digestNotificationSettings);
  const isAccessRequestAlertsEmpty = isEmailAlertsEmpty(accessRequestNotificationSettings);
  const isQuestionAlertsEmpty = isEmailAlertsEmpty(questionNotificationSettings);

  const handleSaveNotificationAlerts = async () => {
    const success = await updateSettings({
      dataroomName,
      isUploadAlertsDirty,
      uploadNotificationSettings: uploadNotificationSettings!,
      isStagingUploadAlertsDirty,
      stagingUploadNotificationSettings: stagingUploadNotificationSettings!,
      isDigestAlertsDirty,
      digestNotificationSettings: digestNotificationSettings!,
      isAccessRequestAlertsDirty,
      accessRequestNotificationSettings: accessRequestNotificationSettings!,
      isQuestionAlertsDirty: !!(isQuestionAlertsDirty || isQuestionRealtimeAlertsDirty),
      questionNotificationSettings: questionNotificationSettings!,
      questionRealtimeNotificationSettings: questionRealtimeNotificationSettings!,
      questionNotificationTypeValue,
      isQuestionManager,
    });

    if (success) {
      getCollection();
      closeModal();
    }
  };

  const content = (
    <div>
      <Alerts
        name="dataroomUploadAlertsForm"
        alertType={ AlertsType.FileUpload }
        withTimezoneLabel
        initialValues={ uploadAlertsInitialValues as IFileUploadNotificationSettingsPayload }
        timezone={ timezone }
        onChange={ setUploadNotificationSettings as (v: IFileUploadNotificationSettingsPayload) => void }
        isFetching={ isFetching }
      />
      { allowedActions.canAccessStaging && (
        <Alerts
          name="dataroomStagingUploadAlertsForm"
          alertType={ AlertsType.StagingFileUpload }
          timezone={ timezone }
          initialValues={ stagingUploadAlertsInitialValues as IStagingFileUploadNotificationSettingsPayload }
          onChange={ setStagingUploadNotificationSettings as (v: IStagingFileUploadNotificationSettingsPayload) => void }
          isFetching={ isFetching }
        />
      ) }
      { allowedActions.canEditDigestAlerts && (
        <DigestAlerts
          timezone={ timezone }
          initialValues={ digestAlertsInitialValues as IDigestNotificationSettingsPayload }
          onChange={ setDigestNotificationSettings as (v: IDigestNotificationSettingsPayload) => void }
          isFetching={ isFetching }
        />
      ) }
      { allowedActions.canEditAccessRequestAlerts && (
        <Alerts
          name="dataroomAccessRequestAlertsForm"
          alertType={ AlertsType.AccessRequest }
          timezone={ timezone }
          initialValues={ accessRequestAlertsInitialValues as IAccessRequestNotificationSettingsPayload }
          onChange={ setAccessRequestNotificationSettings as (v: IAccessRequestNotificationSettingsPayload) => void }
          isFetching={ isFetching }
        />
      ) }
      { allowedActions.canEditQnaAlerts && (
        <QuestionAlerts
          timezone={ timezone }
          initialValues={ questionAlertsInitialValues }
          initialRealtimeValues={ questionRealtimeAlertsInitialValues }
          onChange={ setQuestionNotificationSettings }
          onRealtimeChange={ setQuestionRealtimeNotificationSettings }
          isFetching={ isFetching }
          alertsTypeValue={ questionNotificationTypeValue }
          onTypeChange={ setQuestionNotificationTypeValue }
          isQuestionManager={ isQuestionManager }
          realtimeError={ questionRealtimeValidationError }
          setRealtimeError={ setQuestionRealtimeValidationError }
        />
      ) }
    </div>
  );

  const isAlertsDirty = (
    isUploadAlertsDirty ||
    isStagingUploadAlertsDirty ||
    isDigestAlertsDirty ||
    isAccessRequestAlertsDirty ||
    isQuestionAlertsDirty ||
    isQuestionRealtimeAlertsDirty
  );

  const isAlertsEmpty = (
    isUploadAlertsEmpty ||
    isStagingUploadAlertsEmpty ||
    isDigestAlertsEmpty ||
    isAccessRequestAlertsEmpty ||
    isQuestionAlertsEmpty
  );

  const footer = (
    <div>
      <Button
        type="submit"
        onClick={ handleSaveNotificationAlerts }
        disabled={ !isAlertsDirty || isAlertsEmpty || isFetching || !!questionRealtimeValidationError }
        variant={ ButtonVariantType.action }
        title="Save"
        dataTest="save"
      />
      <Button
        onClick={ closeModal }
        disabled={ isFetching }
        variant={ ButtonVariantType.text }
        title="Cancel"
        dataTest="cancel"
      />
    </div>
  );

  return (
    <Modal
      onCloseClicked={ closeModal }
      isVisible
      title={ `Edit Your Email Alerts for ${ displayName }` }
      footer={ footer }
      className={ styles.modal }
      hideOnOverlayClicked
      onOverlayClicked={ closeModal }
      dataTest="emailAlertsModal"
    >
      { content }
    </Modal>
  );
};

export default (props: React.ComponentProps<typeof AlertsModal>) => (
  <EmailAlertsContext>
    <AlertsModal { ...props } />
  </EmailAlertsContext>
);
